import { zodResolver } from "@hookform/resolvers/zod";
import {
  useCreateTask,
  useGetCompanyDags,
  useGetPythonTaskTemplates,
} from "api/api-hooks/airflow-hooks";
import {
  ControllerDropdownInput,
  ControllerNumberInput,
  ControllerTextInput,
} from "components/basic/controller-inputs/ControllerInputs";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import React, { useState } from "react";
import { Controller, ControllerRenderProps, FieldErrors, useForm } from "react-hook-form";
import { T } from "services/lang/LanguageService";
import { AirflowDag, AirflowTaskDto, AirflowTemplate } from "types/airflow";

import { z } from "zod";

const schema = z.object({
  id: z.object({
    taskId: z.string().regex(/^[a-zA-Z0-9_]+$/, "Airflow id must be alphanumeric with underscores"),
    dagId: z.string(),
  }),
  title: z.string(),
  description: z.optional(z.string()),
  timeout: z.number().nonnegative(),
  template: z.union([
    z.literal("none"),
    z.literal("json"),
    z.literal("dween"),
    z.literal("airflow"),
  ]),
  predefinedTaskTemplate: z.optional(z.string()),
  dweenTaskTemplate: z.optional(z.object({
    id: z.number(),
    title: z.string(),
    taskGroup: z.string(),
    taskCode: z.string(),
  })),
  airflowTaskTemplate: z.optional(
    z
      .object({
        taskId: z.string(),
        dagId: z.string(),
      })
      .transform(data => `${data.dagId}:${data.taskId}`)
  ),
});

const templateOptions = [
  { label: "None", value: "none" },
  { label: "Use user defined template (JSON)", value: "json" },
  { label: "Use DWEEN task as template", value: "dween" },
  { label: "Use Airflow task as template", value: "airflow" },
];

type AirflowTaskFormProps = {
  closeDialog: () => void;
  dag: AirflowDag;
};

const AirflowTaskForm: React.FC<AirflowTaskFormProps> = ({ closeDialog, dag }) => {
  const [template, setTemplate] = useState<AirflowTemplate>("none");
  const createTask = useCreateTask();

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<AirflowTaskDto>({ resolver: zodResolver(schema) });

  const onSubmit = (data: AirflowTaskDto) => {
    createTask.mutate(
      {
        dagId: dag.dagId,
        task: data,
      },
      { onSuccess: closeDialog }
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-wrap">
        <Controller
          name="id.taskId"
          control={control}
          render={({ field }) => (
            <ControllerTextInput field={field} errors={errors} label="Airflow Task ID" />
          )}
        />
        <Controller
          name="id.dagId"
          defaultValue={dag.dagId}
          control={control}
          render={({ field }) => (
            <ControllerTextInput field={field} errors={errors} label="DAG ID" disabled />
          )}
        />
      </div>
      <div className="flex flex-wrap">
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <ControllerTextInput field={field} errors={errors} label="Title" col={8} />
          )}
        />
        <Controller
          name="timeout"
          defaultValue={600}
          control={control}
          render={({ field }) => (
            <ControllerNumberInput
              field={field}
              errors={errors}
              label="Timeout"
              col={4}
              suffix=" s"
            />
          )}
        />
      </div>
      <div className="flex flex-wrap">
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <ControllerTextInput field={field} errors={errors} label="Description" col={12} />
          )}
        />
      </div>
      <div className="px-2 flex gap-2">
        <Controller
          name="template"
          defaultValue="none"
          control={control}
          render={({ field }) => (
            <div className="flex flex-column gap-2 col-6">
              {templateOptions.map(tp => (
                <div key={tp.value} className="flex align-items-center">
                  <RadioButton
                    checked={field.value === tp.value}
                    inputId={tp.value}
                    value={tp.value}
                    onChange={e => {
                      setTemplate(e.value);
                      field.onChange(e.value);
                    }}
                  />
                  <label htmlFor={tp.value} className="ml-2">
                    {tp.label}
                  </label>
                </div>
              ))}
            </div>
          )}
        />
        {template === "dween" && (
          <Controller
            name="dweenTaskTemplate"
            control={control}
            render={({ field }) => <PythonTaskTemplateDropdown field={field} errors={errors} />}
          />
        )}
        {template === "airflow" && (
          <Controller
            name="airflowTaskTemplate"
            control={control}
            render={({ field }) => <AirflowTaskTemplateDropdown field={field} errors={errors} />}
          />
        )}
      </div>
      <div className="flex justify-content-end gap-2 mx-2">
        <Button
          label={T("action.cancel")}
          className="p-button-sm p-button-outlined p-button-secondary"
          type="button"
          onClick={closeDialog}
        />
        <Button label={T("action.create")} className="p-button-sm p-button-success" />
      </div>
    </form>
  );
};

export default AirflowTaskForm;

type TaskTemplateDropdownProps = {
  errors: FieldErrors<AirflowTaskDto>;
  field: ControllerRenderProps<AirflowTaskDto, any>;
};

const PythonTaskTemplateDropdown: React.FC<TaskTemplateDropdownProps> = ({ field, errors }) => {
  const company = useCurrentCompanyContext();
  const { data, isLoading } = useGetPythonTaskTemplates(company?.companyReference ?? "");
  return (
    <ControllerDropdownInput
      field={field}
      errors={errors}
      label="Python task template"
      loading={isLoading}
      options={data}
      optionLabel="taskCode"
      placeholder="Select a template"
      filter
      filterBy="taskCode"
    />
  );
};

const AirflowTaskTemplateDropdown: React.FC<TaskTemplateDropdownProps> = ({ field, errors }) => {
  const { data, isLoading } = useGetCompanyDags();

  return (
    <ControllerDropdownInput
      field={field}
      errors={errors}
      label="Airflow task template"
      loading={isLoading}
      options={data}
      optionLabel="taskId.taskId"
      optionGroupLabel="dagId"
      optionGroupChildren="tasks"
      optionValue="taskId"
      placeholder="Select a template"
      filter
      filterBy="taskId.taskId"
    />
  );
};
